import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Avatar, Badge, Box, Stack, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import { Post } from '../services/postService';
import { storyService } from '../services/storyService';
import StoryViewer, { StoryGroup } from './StoryViewer';
import StoryCreateDialog from './StoryCreateDialog';

function initials(name: string) {
  return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

/** The row of story rings above the feed: yours first (with an add button), then everyone with a live story. */
const StoriesStrip: React.FC<{ currentUserId: string; currentUserName: string }> = ({ currentUserId, currentUserName }) => {
  const [stories, setStories] = useState<Post[]>([]);
  const [viewerStart, setViewerStart] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  // Seen state comes from the server (so it follows you across devices); the viewer adds to it
  // as you watch, and the next load replaces it wholesale.
  const [seen, setSeen] = useState<Set<string>>(new Set());
  const [viewCounts, setViewCounts] = useState<Record<string, number>>({});

  const load = useCallback(() => {
    storyService.list()
      .then(res => { setStories(res.stories); setSeen(new Set(res.seenStoryIds)); setViewCounts(res.viewCounts); })
      .catch(() => setStories([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const markSeen = useCallback((storyId: string) => {
    setSeen(prev => (prev.has(storyId) ? prev : new Set(prev).add(storyId)));
  }, []);

  const groups = useMemo<StoryGroup[]>(() => {
    const byAuthor = new Map<string, StoryGroup>();
    stories.forEach(s => {
      const g = byAuthor.get(s.authorUserId) ?? {
        authorUserId: s.authorUserId,
        authorDisplayName: s.authorDisplayName,
        authorProfilePhotoUrl: s.authorProfilePhotoUrl,
        stories: [],
      };
      g.stories.push(s);
      byAuthor.set(s.authorUserId, g);
    });
    const all = Array.from(byAuthor.values());
    const unseen = (g: StoryGroup) => g.stories.some(s => !seen.has(s.postId));
    // You first, then people with something new, then the rest.
    return all.sort((a, b) =>
      Number(b.authorUserId === currentUserId) - Number(a.authorUserId === currentUserId) ||
      Number(unseen(b)) - Number(unseen(a)));
  }, [stories, seen, currentUserId]);

  const mine = groups.find(g => g.authorUserId === currentUserId);
  const others = groups.filter(g => g.authorUserId !== currentUserId);

  const ringColor = (g: StoryGroup) => (g.stories.some(s => !seen.has(s.postId)) ? 'primary.main' : 'divider');

  // Re-fetch on close so your own view counts (and anything new) are current.
  const closeViewer = () => { setViewerStart(null); load(); };

  const renderGroup = (g: StoryGroup, label: string) => {
    const index = groups.indexOf(g);
    return (
      <Stack key={g.authorUserId} alignItems="center" spacing={0.5} sx={{ cursor: 'pointer', flex: '0 0 auto', width: 68 }} onClick={() => setViewerStart(index)}>
        <Avatar
          src={g.authorProfilePhotoUrl ?? undefined}
          sx={{ width: 56, height: 56, bgcolor: 'primary.main', border: 3, borderColor: ringColor(g), outline: '2px solid', outlineColor: 'background.paper' }}
        >
          {!g.authorProfilePhotoUrl && (initials(g.authorDisplayName) || '?')}
        </Avatar>
        <Typography variant="caption" noWrap sx={{ maxWidth: 68 }}>{label}</Typography>
      </Stack>
    );
  };

  return (
    <Box sx={{ mb: { xs: 2, sm: 3 } }}>
      <Stack direction="row" spacing={1.5} sx={{ overflowX: 'auto', pb: 0.5 }}>
        {mine ? (
          <Stack alignItems="center" spacing={0.5} sx={{ flex: '0 0 auto', width: 68 }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                <Box
                  role="button"
                  aria-label="Add to your story"
                  onClick={(e) => { e.stopPropagation(); setCreateOpen(true); }}
                  sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: 'primary.main', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: 2, borderColor: 'background.paper' }}
                >
                  <Add sx={{ fontSize: 14 }} />
                </Box>
              }
            >
              <Avatar
                src={mine.authorProfilePhotoUrl ?? undefined}
                onClick={() => setViewerStart(groups.indexOf(mine))}
                sx={{ width: 56, height: 56, bgcolor: 'primary.main', border: 3, borderColor: ringColor(mine), cursor: 'pointer' }}
              >
                {!mine.authorProfilePhotoUrl && (initials(currentUserName) || '?')}
              </Avatar>
            </Badge>
            <Typography variant="caption" noWrap>Your story</Typography>
          </Stack>
        ) : (
          <Stack alignItems="center" spacing={0.5} sx={{ cursor: 'pointer', flex: '0 0 auto', width: 68 }} onClick={() => setCreateOpen(true)}>
            <Avatar sx={{ width: 56, height: 56, bgcolor: 'action.hover', color: 'primary.main', border: 2, borderStyle: 'dashed', borderColor: 'primary.main' }}>
              <Add />
            </Avatar>
            <Typography variant="caption" noWrap>Add story</Typography>
          </Stack>
        )}
        {others.map(g => renderGroup(g, g.authorDisplayName.split(' ')[0]))}
      </Stack>

      {viewerStart !== null && (
        <StoryViewer
          groups={groups}
          startGroup={viewerStart}
          currentUserId={currentUserId}
          viewCounts={viewCounts}
          onClose={closeViewer}
          onDeleted={(id) => setStories(prev => prev.filter(s => s.postId !== id))}
          onSeen={markSeen}
        />
      )}
      <StoryCreateDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(s) => setStories(prev => [...prev, s])} />
    </Box>
  );
};

export default StoriesStrip;
